import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MdChevronRight, MdRefresh, MdLink, MdEdit, MdWarning,
  MdCheckCircle, MdHourglassTop, MdCancel, MdEmail,
} from 'react-icons/md';
import Button from './Button';
import { listInvitations, resendInvitation, revokeInvitation, updateInvitationEmail } from '../../services/invitationService';
import { sanitizeEmail, isValidEmail, EMAIL_HINT } from '../../utils/inputRules';
import useImpersonation from '../../hooks/useImpersonation';
import useClubVocab from '../../hooks/useClubVocab';

// ── Delivery status badge ─────────────────────────────────────────────────────
function DeliveryBadge({ inv }) {
  if (inv.status === 'accepted') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
        <MdCheckCircle /> Accepted
      </span>
    );
  }
  if (inv.status === 'expired' || inv.status === 'revoked') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
        {inv.status === 'expired' ? 'Expired' : 'Revoked'}
      </span>
    );
  }
  const ds = inv.emailDeliveryStatus || 'pending';
  const map = {
    delivered:  { label: 'Delivered', cls: 'bg-green-100 text-green-700' },
    sent:       { label: 'Sent',      cls: 'bg-blue-100  text-blue-700'  },
    pending:    { label: 'Pending',   cls: 'bg-amber-500/15 text-amber-400' },
    delayed:    { label: 'Delayed',   cls: 'bg-amber-500/15 text-amber-400' },
    bounced:    { label: 'Bounced',   cls: 'bg-red-100   text-red-700'   },
    failed:     { label: 'Failed',    cls: 'bg-red-100   text-red-700'   },
    suppressed: { label: 'Suppressed',cls: 'bg-nav-hover text-muted'  },
  };
  const { label, cls } = map[ds] || map.pending;
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {label}
    </span>
  );
}

// ── Edit email inline form ────────────────────────────────────────────────────
function EditEmailRow({ inv, onDone }) {
  const { isReadOnly } = useImpersonation();
  const [email, setEmail] = useState(inv.email);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => updateInvitationEmail(inv._id, email.trim().toLowerCase()),
    onSuccess: () => {
      toast.success('Email updated and invite resent');
      qc.invalidateQueries({ queryKey: ['invitations'] });
      onDone();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update email'),
  });

  const emailValid = email.trim() !== '' && isValidEmail(email);
  const emailErr   = email.trim() !== '' && !isValidEmail(email);

  return (
    <form
      className="mt-1"
      onSubmit={(e) => { e.preventDefault(); if (emailValid) mutation.mutate(); }}
    >
      <div className="flex items-center gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(sanitizeEmail(e.target.value))}
          className={`flex-1 px-2.5 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 ${emailErr ? 'border-red-400' : 'border-gray-200'}`}
          placeholder="corrected@email.com"
        />
        <Button type="submit" loading={mutation.isPending} disabled={!emailValid || isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className="py-1.5 px-3 text-xs">Save & Resend</Button>
        <button type="button" onClick={onDone} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
      </div>
      {emailErr && <p className="text-xs text-red-500 mt-1">{EMAIL_HINT}</p>}
    </form>
  );
}

// ── Relative time helper ──────────────────────────────────────────────────────
function relTime(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function PendingInvitationsPanel({ role }) {
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(null);
  const qc = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['invitations', role],
    queryFn: () => listInvitations({ role, status: 'pending' }).then(r => r.data),
    enabled: open,
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });

  const invitations = data?.invitations || [];
  const hasBounced  = invitations.some(i =>
    ['bounced','failed','suppressed'].includes(i.emailDeliveryStatus)
  );

  const resendMutation = useMutation({
    mutationFn: (id) => resendInvitation(id),
    onSuccess: () => {
      toast.success('Invite resent');
      qc.invalidateQueries({ queryKey: ['invitations', role] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to resend'),
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => revokeInvitation(id),
    onSuccess: () => {
      toast.success('Invitation revoked');
      qc.invalidateQueries({ queryKey: ['invitations', role] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to revoke'),
  });

  const copyLink = (token) => {
    const link = `${window.location.origin}/accept-invite/${token}`;
    navigator.clipboard.writeText(link).then(() => toast.success('Invite link copied'));
  };

  // 'student' reads as "Member" for gyms/yoga (vocab); admin/trainer keep their labels.
  const roleLabel = role === 'admin' ? 'Admin' : role === 'trainer' ? 'Trainer' : v.member;

  return (
    <div className="mt-4">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-secondary transition-colors"
      >
        <MdHourglassTop className="text-base" />
        Pending {roleLabel} Invitations
        {hasBounced && !open && (
          <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Some invites have delivery issues" />
        )}
        <MdChevronRight className={`text-base transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="mt-3 border border-gray-100 rounded-xl overflow-hidden">
          {/* Bounced warning banner */}
          {hasBounced && (
            <div className="flex items-start gap-2 px-4 py-2.5 bg-red-50 border-b border-red-100">
              <MdWarning className="text-red-500 text-base flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">
                One or more invites could not be delivered. Please check the email address or share the invite link manually.
              </p>
            </div>
          )}

          {isPending ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">Loading…</div>
          ) : invitations.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <MdEmail className="text-3xl text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No pending invitations</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {invitations.map(inv => {
                const isBad = ['bounced','failed','suppressed'].includes(inv.emailDeliveryStatus);
                return (
                  <li key={inv._id} className={`px-4 py-3 ${isBad ? 'bg-red-50/50' : 'bg-white'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-secondary truncate">{inv.email}</span>
                          <DeliveryBadge inv={inv} />
                        </div>
                        {inv.lastEmailSentAt && (
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            Sent {relTime(inv.lastEmailSentAt)}
                            {inv.resendCount > 1 ? ` · ${inv.resendCount}× sent` : ''}
                          </p>
                        )}
                        {isBad && (
                          <p className="text-[11px] text-red-600 mt-0.5">
                            Email could not be delivered. Check address or share the link manually.
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          title="Copy invite link"
                          onClick={() => copyLink(inv.token)}
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-primary transition-colors"
                        >
                          <MdLink className="text-base" />
                        </button>
                        <button
                          title={isReadOnly ? 'Disabled: read-only support mode' : 'Edit email address'}
                          disabled={isReadOnly}
                          onClick={() => setEditing(editing === inv._id ? null : inv._id)}
                          className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            editing === inv._id
                              ? 'bg-primary/10 text-primary'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-primary'
                          }`}
                        >
                          <MdEdit className="text-base" />
                        </button>
                        <button
                          title={isReadOnly ? 'Disabled: read-only support mode' : 'Resend invitation'}
                          disabled={resendMutation.isPending || isReadOnly}
                          onClick={() => resendMutation.mutate(inv._id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdRefresh className="text-base" />
                        </button>
                        <button
                          title={isReadOnly ? 'Disabled: read-only support mode' : 'Revoke invitation'}
                          disabled={revokeMutation.isPending || isReadOnly}
                          onClick={() => revokeMutation.mutate(inv._id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdCancel className="text-base" />
                        </button>
                      </div>
                    </div>

                    {editing === inv._id && (
                      <EditEmailRow inv={inv} onDone={() => setEditing(null)} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
